import React, { useState, useEffect } from "react";
import "./IntegrationModal.css";
import customFetch from "../../../../helpers/axios";
import Combobox from "../../../components/Combobox/Combobox";
import ButtonClose from "../../../components/Buttons/ButtonClose/ButtonClose";
import ButtonDelete from "../../../components/Buttons/ButtonDelete/ButtonDelete";
import ButtonCancel from "../../../components/Buttons/ButtonCancel/ButtonCancel";
import ButtonSave from "../../../components/Buttons/ButtonSave/ButtonSave";
import { Plug, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import useUser from "../../../../hooks/useUser";

export default function IntegrationModal({
  isOpen,
  onClose,
  integration,
  onSave,
  mode = "add",
  configData = null,
  isSaving = false,
  existingIntegrations = [],
  onDelete = null,
  isDeleting = false,
}) {
  const { t } = useTranslation('integrations');
  const [formData, setFormData] = useState({
    is_active: true,
    base_url: "",
    secret_key: "",
    client_id: "",
    callback_url: "",
    other_urls: "",
    platform: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [platformList, setPlatformList] = useState([]); // options for combobox
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copiedCb, setCopiedCb] = useState(false);
  const { user } = useUser();
  const token = user?.access || null;

  const stripProtocol = (url) => {
    return String(url || "").replace(/^https?:\/\//i, "");
  };

  const ensureHttps = (url) => {
    const trimmed = String(url || "").trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const buildCallbackUrl = () => {
    const isProd = process.env.NODE_ENV === "production";
    const envBase = isProd
      ? process.env.REACT_APP_BASE_URL_PROD
      : process.env.REACT_APP_BASE_URL_DEV || process.env.REACT_APP_BASE_URL_PROD;

    const trimmed = String(envBase || "").trim();
    if (!trimmed) return "";

    // Asegurar que tiene protocolo
    const withProtocol = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;

    const normalized = withProtocol.replace(/\/+$/, "");
    return `${normalized}/api/auth/callback/`;
  };

  // En modo add: cargar opciones del dropdown
  useEffect(() => {
    if (!token || mode !== "add") return;

    const fetchDropdownOptions = async () => {
      setLoadingPlatforms(true);
      try {
        const response = await customFetch.get(
          "/api/integrations/integration/dropdown/",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const integrations = response.data.results || response.data || [];
        
        // Crear sets de IDs y nombres de integraciones existentes
        const existingArray = Array.isArray(existingIntegrations) ? existingIntegrations : [];
        const existingIds = new Set(
          existingArray
            .map((i) => i.integration?.id || i.integration)
            .filter((id) => id !== undefined && id !== null)
        );
        const existingNames = new Set(
          existingArray
            .map((i) => 
              i.integration?.name || 
              i.integration_name || 
              i.name || 
              i.display_name
            )
            .filter(Boolean)
            .map((s) => String(s).trim().toLowerCase())
        );
        
        // Construir opciones para el dropdown
        const options = integrations.map((integration) => {
          const label = integration.display_name || integration.name || "Sin nombre";
          const byId = existingIds.has(integration.id);
          const byName = existingNames.has(String(label).trim().toLowerCase());
          return {
            value: integration.id?.toString() || integration.name,
            label,
            disabled: byId || byName, // Deshabilitar si ya existe
          };
        });
        
        setPlatformList(options);
      } catch (error) {
        console.error("Error fetching integration dropdown:", error);
        setPlatformList([]);
      } finally {
        setLoadingPlatforms(false);
      }
    };

    fetchDropdownOptions();
  }, [token, mode, existingIntegrations]);

  useEffect(() => {
    if (mode === "add") {
      // Elegir la primera opción habilitada
      const firstEnabled = platformList.find((o) => !o.disabled);
      setFormData({
        is_active: true,
        base_url: "",
        secret_key: "",
        client_id: "",
        callback_url: buildCallbackUrl(),
        other_urls: "",
        platform: firstEnabled ? firstEnabled.value : "",
      });
    } else if (mode === "configure" && configData) {
      // Stringify other_urls if it comes as an object so the textarea shows JSON instead of [object Object]
      let otherUrlsStr = "";
      if (configData.other_urls != null) {
        if (typeof configData.other_urls === "string") {
          // Try to pretty-print if it's valid JSON string; otherwise keep as-is
          try {
            const parsed = JSON.parse(configData.other_urls);
            otherUrlsStr = JSON.stringify(parsed, null, 2);
          } catch {
            otherUrlsStr = configData.other_urls;
          }
        } else if (typeof configData.other_urls === "object") {
          otherUrlsStr = JSON.stringify(configData.other_urls, null, 2);
        } else {
          otherUrlsStr = String(configData.other_urls);
        }
      }
      setFormData((prev) => ({
        ...prev,
        base_url: stripProtocol(configData.base_url || ""),
        secret_key: configData.secret_key || "",
        client_id: configData.client_id || "",
        callback_url: configData.callback_url || "",
        other_urls: otherUrlsStr,
        platform: configData.platform || "",
      }));
    }
    
    // Limpiar errores al abrir el modal
    setFieldErrors({});
  }, [isOpen, mode, configData, integration, platformList]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateConfigFields = () => {
    const errors = {};
    if (!formData.base_url) errors.base_url = [t('modal.errors.required')];
    // secret_key and client_id are now optional
    if (mode === "add" && !formData.platform)
      errors.platform = [t('modal.errors.required')];
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validar campos config en ambos modos
    if (!validateConfigFields()) return;
    
    // Función para manejar errores del backend
    const handleBackendErrors = (errorData) => {
      const backendErrors = {};
      
      // Si errorData es un objeto con campos de error
      if (typeof errorData === 'object' && errorData !== null) {
        Object.keys(errorData).forEach(field => {
          if (field === 'non_field_errors') {
            // Errores generales (restricciones de modelo, etc.)
            if (Array.isArray(errorData[field]) && errorData[field].length > 0) {
              backendErrors.general = [errorData[field][0]];
            }
          } else if (Array.isArray(errorData[field]) && errorData[field].length > 0) {
            // Tomar solo el primer mensaje de error de campos específicos
            backendErrors[field] = [errorData[field][0]];
          } else if (typeof errorData[field] === 'string') {
            backendErrors[field] = [errorData[field]];
          }
        });
      }
      
      // Si no se pudieron procesar los errores del backend, mostrar error genérico
      if (Object.keys(backendErrors).length === 0) {
        backendErrors.general = [t('modal.errors.general')];
      }
      
      setFieldErrors(backendErrors);
    };
    
    try {
      if (mode === "add") {
        // En modo add, también validar y parsear other_urls a objeto JSON si viene como string
        let otherUrlsParsed = {};
        if (typeof formData.other_urls === "string" && formData.other_urls.trim() !== "") {
          try {
            otherUrlsParsed = JSON.parse(formData.other_urls);
            if (otherUrlsParsed === null || typeof otherUrlsParsed !== "object" || Array.isArray(otherUrlsParsed)) {
              throw new Error("other_urls must be a JSON object");
            }
          } catch (e) {
            setFieldErrors((prev) => ({
              ...prev,
              other_urls: [t('modal.errors.invalidJson')],
            }));
            return;
          }
        } else if (typeof formData.other_urls === "object" && formData.other_urls !== null) {
          otherUrlsParsed = formData.other_urls;
        }

        const { callback_url: _omitCbAdd, ...restAdd } = formData;
        const payloadAdd = {
          ...restAdd,
          base_url: ensureHttps(restAdd.base_url),
          other_urls: otherUrlsParsed,
        };

        await onSave(payloadAdd, handleBackendErrors);
        onClose();
      } else {
        // Solo pasar campos de config en configure
        // Parse other_urls JSON string into object; validate before sending
        let otherUrlsParsed = {};
        if (typeof formData.other_urls === "string" && formData.other_urls.trim() !== "") {
          try {
            otherUrlsParsed = JSON.parse(formData.other_urls);
            if (otherUrlsParsed === null || typeof otherUrlsParsed !== "object" || Array.isArray(otherUrlsParsed)) {
              throw new Error("other_urls must be a JSON object");
            }
          } catch (e) {
            setFieldErrors((prev) => ({
              ...prev,
              other_urls: [t('modal.errors.invalidJson')],
            }));
            return;
          }
        } else if (typeof formData.other_urls === "object" && formData.other_urls !== null) {
          otherUrlsParsed = formData.other_urls;
        }

        const configPayload = {
          base_url: ensureHttps(formData.base_url),
          secret_key: formData.secret_key,
          client_id: formData.client_id,
          // callback_url is not sent to backend (read-only)
          other_urls: otherUrlsParsed,
        };
        await onSave(configPayload, handleBackendErrors);
        onClose();
      }
    } catch (error) {
      // Si onSave rechaza la promesa, no cerrar el modal
      console.error('Error saving:', error);
    }
  };
  
  // Copy callback_url to clipboard with feedback
  const handleCopyCallback = async () => {
    try {
      const text = formData.callback_url || "";
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedCb(true);
      setTimeout(() => setCopiedCb(false), 1500);
    } catch (_) {
      // ignore
    }
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    await onDelete(integration);
    setShowDeleteConfirm(false);
  };

  if (!isOpen) return null;

  return (
    <div className="integration-modal-overlay">
      <div className="integration-modal">
        <div className="integration-modal-header">
          <div className="integration-modal-title-section">
            <Plug size={20} aria-hidden="true" stroke="var(--primary)" />
            <div className="integration-modal-title">
              {mode === "add" ? t('modal.titleAdd') : t('modal.titleConfigure')}
            </div>
          </div>
          <ButtonClose onClick={onClose} ariaLabel={t('modal.closeAria')}/>
        </div>
        <form onSubmit={handleSubmit} className="integration-modal-form">
          {/* Platform field en modo configure - en la parte superior */}
          {mode === "configure" && (
            <div className="integration-modal-field">
              <label style={{ fontWeight: "bold" }}>
                {t('modal.integrationLabel', { name: (configData?.integration?.name || t('modal.integrationNoName')) })}
              </label>
            </div>
          )}

          {/* Platform field en modo add */}
          {mode === "add" && (
            <>
              <div className="integration-modal-field">
                <label>{t('modal.platformLabel')}</label>
                {loadingPlatforms ? (
                  <div className="integration-modal-input">
                    {t('modal.platformLoading')}
                  </div>
                ) : (
                  <Combobox
                    value={formData.platform}
                    onChange={(value) => handleChange("platform", value)}
                    options={platformList}
                    label={t('modal.platformSelect')}
                    className={[
                      "combobox-full",
                      fieldErrors.platform ? "integration-modal-input-error" : ""
                    ].join(" ").trim()}
                    buttonClass="integration-modal-input"
                  />
                )}
                {fieldErrors.platform && (
                  <div className="integration-modal-error">
                    {fieldErrors.platform[0]}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Config fields (siempre en ambos modos) */}
          <div className="integration-modal-field">
            <label>{t('modal.fields.baseUrl')}</label>
            <input
              type="text"
              value={formData.base_url}
              onChange={(e) => {
                const raw = e.target.value;
                const value = stripProtocol(raw);
                handleChange("base_url", value);
              }}
              className={`integration-modal-input${
                fieldErrors.base_url ? " integration-modal-input-error" : ""
              }`}
              required
              placeholder={t('modal.fields.baseUrlPlaceholder')}
            />
            {fieldErrors.base_url && (
              <div className="integration-modal-error">
                {fieldErrors.base_url[0]}
              </div>
            )}
          </div>
          <div className="integration-modal-field">
            <label>{t('modal.fields.clientId')}</label>
            <input
              type="text"
              value={formData.client_id}
              onChange={(e) => handleChange("client_id", e.target.value)}
              className="integration-modal-input"
            />
            {fieldErrors.client_id && (
              <div className="integration-modal-error">
                {fieldErrors.client_id[0]}
              </div>
            )}
          </div>
          <div className="integration-modal-field">
            <label>{t('modal.fields.secretKey')}</label>
            <input
              type="text"
              value={formData.secret_key}
              onChange={(e) => handleChange("secret_key", e.target.value)}
              className="integration-modal-input"
            />
            {fieldErrors.secret_key && (
              <div className="integration-modal-error">
                {fieldErrors.secret_key[0]}
              </div>
            )}
          </div>
          
          <div className="integration-modal-field">
            <label>{t('modal.fields.callbackUrl')}</label>
            <div
              className={`integration-input-wrapper ${copiedCb ? 'show-badge' : ''}`}
              data-tooltip={formData.callback_url || ''}
            >
              <input
                type="text"
                value={formData.callback_url}
                onChange={(e) => handleChange("callback_url", e.target.value)}
                className={`integration-modal-input integration-modal-input-readonly${
                  fieldErrors.callback_url ? " integration-modal-input-error" : ""
                }`}
                readOnly
                placeholder={t('modal.fields.callbackUrlPlaceholder')}
              />
              <button
                type="button"
                className="integration-copy-icon"
                onClick={handleCopyCallback}
                aria-label={t('modal.copy', { defaultValue: 'Copy to clipboard' })}
              >
                <Copy size={16} />
              </button>
              <span className="integration-copied-badge">{t('modal.copied', { defaultValue: 'Copied!' })}</span>
            </div>
            {fieldErrors.callback_url && (
              <div className="integration-modal-error">
                {fieldErrors.callback_url[0]}
              </div>
            )}
          </div>
          <div className="integration-modal-field">
            <label>{t('modal.fields.otherUrls')}</label>
            <textarea
              value={formData.other_urls}
              onChange={(e) => handleChange("other_urls", e.target.value)}
              className={`integration-modal-input${
                fieldErrors.other_urls ? " integration-modal-input-error" : ""
              }`}
              placeholder={t('modal.fields.otherUrlsPlaceholder')}
              rows={5}
              style={{ fontFamily: "monospace", resize: "vertical" }}
            />
            {fieldErrors.other_urls && (
              <div className="integration-modal-error">
                {fieldErrors.other_urls[0]}
              </div>
            )}
          </div>
          <div className={`integration-modal-actions ${mode === "configure" && onDelete ? "has-delete" : ""}`}>
            {mode === "configure" && onDelete && (
              <ButtonDelete
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSaving || isDeleting}
                ariaLabel={t('modal.delete.ariaLabel')}
              >
                {t('modal.delete.button')}
              </ButtonDelete>
            )}
            <div className="integration-modal-actions-right">
              <ButtonCancel onClick={onClose} disabled={isSaving || isDeleting} />
              <ButtonSave
                type="submit"
                isEdit={mode === "configure"}
                isLoading={isSaving}
                disabled={isSaving || isDeleting}
              />
            </div>
          </div>
        </form>
        {showDeleteConfirm && (
          <div className="integration-delete-overlay" role="dialog" aria-modal="true">
            <div className="integration-delete-dialog">
              <h3 className="integration-delete-title">{t('modal.delete.title')}</h3>
              <p className="integration-delete-message">
                {t('modal.delete.message')}
              </p>
              <div className="integration-delete-actions">
                <ButtonCancel onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting} />
                <ButtonDelete onClick={handleConfirmDelete} disabled={isDeleting}>
                  {isDeleting ? t('modal.delete.progress') : t('modal.delete.button')}
                </ButtonDelete>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
